import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ClockIcon, DollarSignIcon, GraduationCapIcon, LayersIcon, ShieldCheckIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { LegalFooter } from '../components/LegalFooter';
import { buildFindDegreesClickout } from '../services/mediaMatchers/clickout';
import { getAreaHeroImage, getRelatedLevels, getRelatedPrograms, type DegreeProgramCombo } from '../utils/degreeSlugs';
interface DegreeProgramLandingPageProps {
  combo: DegreeProgramCombo;
}

/**
 * Generated landing page for one degree level × program combination.
 *
 * The copy is assembled from the combination itself rather than hand-written,
 * so every program has a page without claiming specifics — no invented tuition
 * figures, salaries, school names, or program lengths.
 */
export function DegreeProgramLandingPage({
  combo
}: DegreeProgramLandingPageProps) {
  const location = useLocation();
  const heroImage = getAreaHeroImage(combo.areaStudy);
  usePageMeta(`Online ${combo.title} Programs | Online College Guide`, `Compare accredited online ${combo.programLabel} programs. Get matched by career goals, schedule, and budget.`);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [combo.slug]);
  const ctaHref = buildFindDegreesClickout(location.search, {
    degreeInterest: combo.degreeInterest,
    areaStudy: combo.areaStudy,
    mProgram: combo.mProgram
  });
  const relatedLevels = getRelatedLevels(combo);
  const relatedPrograms = getRelatedPrograms(combo);
  const reasons = [{
    icon: ShieldCheckIcon,
    title: 'Accredited schools only',
    body: `Every ${combo.programLabel} program we match you with comes from an accredited institution, so your credits and credential hold their value.`
  }, {
    icon: ClockIcon,
    title: 'Built around your schedule',
    body: 'Online coursework lets you keep working while you study, with start dates throughout the year rather than one intake each fall.'
  }, {
    icon: DollarSignIcon,
    title: 'Aid you may qualify for',
    body: 'Accredited schools eligible for federal aid can accept the FAFSA. Ask each school what applies to your situation once you are matched.'
  }];
  return <main className="bg-white" data-id="element-1346" data-mp="tjszz82henxl">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-100" data-id="element-1347" data-mp="5t5pcf16lk7c">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-1348" data-mp="5rkvysqls7b2">
          <Link to="/" className="flex items-center" data-id="element-1349" data-mp="shshcq9u957">
            <img src="https://cdn.magicpatterns.com/uploads/hM6pwxGTxH7R3Ug6xJ6sm8/Horizontal_OCG_logo.png" alt="Online College Guide - Your Path Forward" className="h-8 md:h-10 w-auto object-contain" data-id="element-1350" data-mp="fkjup3ftde8e" />
          </Link>
          <div className="hidden sm:flex items-center text-xs font-semibold text-gray-500 tracking-wider uppercase" data-id="element-1351" data-mp="3e9qufkqozt56">
            <ShieldCheckIcon className="w-4 h-4 mr-1.5 text-green-600" data-id="element-1352" data-mp="1tr6kw3g8ptx9" />
            Trusted by 2M+ Students
          </div>
        </div>
      </div>

      {/* Hero — photography of the work this field leads to, behind a navy
          scrim so the headline stays legible over the image. */}
      <header className={heroImage ? 'relative bg-navy' : 'hero-bg'} style={heroImage ? {
      backgroundImage: `linear-gradient(to right, rgba(10,31,68,0.94), rgba(10,31,68,0.80)), url(${heroImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center right'
    } : undefined} data-id="element-1353" data-mp="1orlifj1v33fs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20" data-id="element-1354" data-mp="27i379cjkpevv">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-1355" data-mp="s2cga5d43acm">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-1356" data-mp="s0t16i36z5cf">
              <li data-id="element-1357" data-mp="w595wg12k6q2">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-1358" data-mp="w1lbzfrppiat">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1359" data-mp="17jlw2qthrcja">/</li>
              <li data-id="element-1360" data-mp="17isyoggf9zdt">
                <Link to="/find-degrees" className="hover:text-white transition-colors" data-id="element-1361" data-mp="17iat2t7ve8t8">
                  Find Degrees
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1362" data-mp="1v9ojuuqdhwc3">/</li>
              <li className="text-white/90" data-id="element-1363" data-mp="1v8dit2obb2oo">{combo.areaLabel}</li>
            </ol>
          </nav>

          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start" data-id="element-1364" data-mp="1qbn16awuzz15">
            <div className="order-2 lg:order-1 lg:col-span-3" data-id="element-1365" data-mp="1lazbptoa3q91">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-gold mb-4" data-id="element-1366" data-mp="1ldd7k4qs96hn">
                {combo.degreeLabel} · {combo.areaLabel}
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5" data-id="element-1367" data-mp="9xwwb1xx16qg">
                Online {combo.title} Programs
              </h1>
              <p className="text-lg text-white/80 leading-relaxed max-w-2xl mb-8" data-id="element-1368" data-mp="2hiws6sy9daho">
                Compare accredited online {combo.programLabel.toLowerCase()}{' '}
                programs at the {combo.degreeLabel.toLowerCase()} level. Tell us
                your goals and we'll connect you with schools that fit your
                schedule and budget — at no cost, with no obligation.
              </p>
              <ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/80" data-id="element-1369" data-mp="307jwvacivxuq">
                {['Accredited', 'Flexible online study', 'No obligation'].map(item => <li key={item} className="inline-flex items-center gap-2" data-id="element-1370" data-mp="39acpbgxfyfm5">
                      <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-1371" data-mp="3e8wa3e0atlcx" />
                      {item}
                    </li>)}
              </ul>
            </div>

            {/* CTA card */}
            <div className="order-1 lg:order-2 lg:col-span-2 bg-white rounded-2xl shadow-2xl ring-4 ring-white/20 p-8 sm:p-9 text-center" data-id="element-1372" data-mp="2n6al3vkp5qjo">
              <GraduationCapIcon className="w-11 h-11 text-navy mx-auto mb-5" aria-hidden="true" data-id="element-1373" data-mp="2i8irmx4elwdd" />
              <h2 className="text-2xl font-extrabold text-navy leading-tight mb-3" data-id="element-1374" data-mp="2r6kxbpz31d6z">
                Find {combo.programLabel} Colleges
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-7" data-id="element-1375" data-mp="361s8nmguz9k9">
                We'll show you accredited schools offering{' '}
                {combo.programLabel.toLowerCase()} programs.
              </p>
              <a href={ctaHref} className="group w-full inline-flex items-center justify-center gap-2 bg-cta hover:bg-cta-dark text-white font-extrabold py-5 px-6 rounded-xl text-lg shadow-lg hover:shadow-xl transition-[background-color,box-shadow] duration-200 focus:outline-none focus:ring-4 focus:ring-cta/40" data-id="element-1376" data-mp="3av3i7vd224fv">
                Get Matched Now
                <ArrowRightIcon className="w-6 h-6 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-1377" data-mp="cb0eo11e9bti" />
              </a>
              <p className="mt-5 text-sm text-gray-500" data-id="element-1378" data-mp="2cuj0166l1x3">
                Takes about 2 minutes
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Why online */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20" data-id="element-1379" data-mp="3phk2hynpp07w">
        <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-3 text-center" data-id="element-1380" data-mp="3pjdb3jhvxuij">
          Why study {combo.programLabel.toLowerCase()} online?
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed text-center" data-id="element-1381" data-mp="3fsio2cwteqw1">
          An online {combo.degreeLabel.toLowerCase()} lets you build
          credentials in {combo.areaLabel.toLowerCase()} without leaving your
          job. What matters to employers is the school's accreditation and the
          skills you gain, not whether you attended in person.
        </p>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-1382" data-mp="3hb0rphhw7hh7">
          {reasons.map(reason => <div key={reason.title} className="flex flex-col items-center text-center" data-id="element-1383" data-mp="3ca3ap88mctha">
              <reason.icon className="w-6 h-6 text-crimson mb-4" aria-hidden="true" data-id="element-1384" data-mp="37cb91mi69szw" />
              <h3 className="text-base font-bold text-navy mb-2" data-id="element-1385" data-mp="33azovwi2hzz3">
                {reason.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1386" data-mp="2yi6nnilt59rr">
                {reason.body}
              </p>
            </div>)}
        </div>
      </section>

      {/* Related */}
      {(relatedLevels.length > 0 || relatedPrograms.length > 0) && <section className="bg-light border-y border-gray-200" data-id="element-1387" data-mp="2jl5urbrvr8rp">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 space-y-12" data-id="element-1388" data-mp="2fkvody06egcb">
            {relatedLevels.length > 0 && <div data-id="element-1389" data-mp="v1t98th68qjt">
                <h2 className="text-xl sm:text-2xl font-bold text-navy mb-1.5" data-id="element-1390" data-mp="v2bf8htmyj4x">
                  {combo.programLabel} at other levels
                </h2>
                <p className="text-sm text-gray-600 mb-6" data-id="element-1391" data-mp="142rcvanp6w1v">
                  Same field, different credential. Compare how far each level
                  takes you.
                </p>
                <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1392" data-mp="bevm0mkt7yd0">
                  {relatedLevels.map(item => <li key={item.slug} data-id="element-1393" data-mp="geqaykl0ydvf">
                      <Link to={`/find-degrees/${item.slug}`} className="group flex flex-col h-full bg-white border border-gray-200 rounded-xl p-5 hover:border-navy hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-1394" data-mp="gbm2hb0j0vyy">
                        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-crimson mb-2" data-id="element-1395" data-mp="3trjoyly53jrz">
                          <LayersIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1396" data-mp="22j8zjgxmj2oj" />
                          {item.degreeLabel}
                        </span>
                        <span className="text-base font-semibold text-navy leading-snug mb-4" data-id="element-1397" data-mp="2chf3s56wkkwk">
                          {item.programLabel}
                        </span>
                        <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-navy" data-id="element-1398" data-mp="1jt9hvf444pv5">
                          View programs
                          <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-1399" data-mp="1osm6o5ejo70d" />
                        </span>
                      </Link>
                    </li>)}
                </ul>
              </div>}
            {relatedPrograms.length > 0 && <div data-id="element-1400" data-mp="15z9v2dqcjynp">
                <h2 className="text-xl sm:text-2xl font-bold text-navy mb-1.5" data-id="element-1401" data-mp="3jbmbeq1ahb2h">
                  Other {combo.areaLabel.toLowerCase()} programs
                </h2>
                <p className="text-sm text-gray-600 mb-6" data-id="element-1402" data-mp="3f86fjvx1nequ">
                  Still deciding? These {combo.degreeLabel.toLowerCase()}{' '}
                  programs sit in the same field.
                </p>
                <ul className="flex flex-wrap gap-2.5" data-id="element-1403" data-mp="35eh8gywvgsbg">
                  {relatedPrograms.map(item => <li key={item.slug} data-id="element-1404" data-mp="2vmbks1dnkx22">
                      <Link to={`/find-degrees/${item.slug}`} className="group inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full pl-4 pr-3 py-2.5 text-sm font-medium text-navy hover:border-navy hover:shadow-sm transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-1405" data-mp="2vl8rjmsuyiiz">
                        {item.programLabel}
                        <ArrowRightIcon className="w-3.5 h-3.5 text-gray-400 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-navy" aria-hidden="true" data-id="element-1406" data-mp="22ztlzftt6vc3" />
                      </Link>
                    </li>)}
                </ul>
              </div>}
          </div>
        </section>}

      {/* Closing CTA */}
      <section className="bg-navy" data-id="element-1407" data-mp="2lrmg5dkm6mle">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-id="element-1408" data-mp="2lsnr7i1mr934">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" data-id="element-1409" data-mp="2qomjvhtzrd9c">
            Ready to compare {combo.programLabel.toLowerCase()} programs?
          </h2>
          <p className="text-white/80 leading-relaxed mb-8" data-id="element-1410" data-mp="2gyjjgwkjix0z">
            Answer a few questions and we'll match you with accredited schools
            offering online {combo.degreeLabel.toLowerCase()} programs. No cost
            to students, with no obligation to enroll.
          </p>
          <a href={ctaHref} className="group inline-flex items-center justify-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-4 px-10 rounded-lg text-base transition-colors duration-200 shadow-lg" data-id="element-1411" data-mp="y055ftsceppo">
            Get Matched Now
            <ArrowRightIcon className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-1412" data-mp="12rn4udepwi8j" />
          </a>
          <p className="mt-5 text-xs text-white/50 uppercase tracking-widest" data-id="element-1413" data-mp="f3vv9hkkajay">
            No cost · No obligation · Takes 2 minutes
          </p>
        </div>
      </section>

      <LegalFooter data-id="element-1414" data-mp="p1th5yey840e" />
    </main>;
}